import React, { useState } from 'react';
import {
  Modal,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import {
  X,
  AlertTriangle,
  Trash2,
  Users,
  Settings,
  DollarSign,
  Globe,
} from 'lucide-react';
import { useCommercialPlan } from 'contexts';
import { ICommercialPlanViewModel } from 'viewModels';
import { formatCurrency } from 'utils/formatCurrency';
import {
  ModalWrapper,
  ModalHeader,
  ModalTitleContainer,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  CloseButton,
  WarningAlert,
  ErrorAlert,
  LoadingContainer,
  LoadingText,
} from './modalStyles';

interface DeletePlanModalProps {
  open: boolean;
  onClose: () => void;
  plan: ICommercialPlanViewModel;
  onSuccess: () => void;
}

const DeletePlanModal: React.FC<DeletePlanModalProps> = ({
  open,
  onClose,
  plan,
  onSuccess,
}) => {
  const { handleRemovePlan } = useCommercialPlan();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    // Impedir exclusão do plano padrão
    if (plan.isDefault) {
      setError(
        'Não é possível excluir o plano padrão. Defina outro plano como padrão primeiro.'
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const success = await handleRemovePlan(plan.id);
      if (success) {
        onSuccess();
      } else {
        setError('Erro ao excluir plano');
      }
    } catch (err) {
      setError('Erro ao excluir plano');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableScrollLock={false}
      keepMounted={false}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <ModalWrapper
        style={{
          maxHeight: 'fit-content',
          height: 'auto',
          display: 'block',
        }}
      >
        <ModalHeader>
          <ModalTitleContainer>
            <ModalTitle id="modal-title">
              Excluir {plan?.name}
            </ModalTitle>
          </ModalTitleContainer>
          <CloseButton onClick={handleClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalContent
          style={{
            padding: '16px',
            flex: 'none',
            overflow: 'visible',
            display: 'block',
          }}
        >
          {/* Erro de Validação */}
          {error && (
            <ErrorAlert
              style={{
                padding: '8px 12px',
                marginBottom: '12px',
              }}
            >
              <AlertTriangle size={16} />
              {error}
            </ErrorAlert>
          )}

          {/* Aviso para Plano Padrão */}
          {plan.isDefault && (
            <WarningAlert
              style={{
                padding: '8px 12px',
                marginBottom: '12px',
              }}
            >
              <AlertTriangle size={16} />
              <div>
                <strong>Plano Padrão:</strong> Este plano não pode ser
                excluído. Defina outro plano como padrão primeiro.
              </div>
            </WarningAlert>
          )}

          {/* Pergunta de Confirmação */}
          <Box sx={{ textAlign: 'center', py: '8px' }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1rem',
                fontWeight: 500,
                color: '#374151',
                mb: '8px',
              }}
            >
              Tem certeza que deseja excluir o plano "{plan?.name}"?
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.875rem' }}
            >
              Esta ação não pode ser desfeita.
            </Typography>
          </Box>
        </ModalContent>

        <ModalFooter style={{ padding: '12px 16px' }}>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleDelete}
            disabled={loading || plan.isDefault}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              background: plan.isDefault ? '#9ca3af' : '#dc2626',
              color: 'white',
              cursor: plan.isDefault ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: loading || plan.isDefault ? 0.5 : 1,
            }}
          >
            {loading ? (
              <LoadingContainer>
                <CircularProgress size={16} />
                <LoadingText>Excluindo...</LoadingText>
              </LoadingContainer>
            ) : (
              <>
                <Trash2 size={14} />
                {plan.isDefault ? 'Não Permitido' : 'Excluir'}
              </>
            )}
          </button>
        </ModalFooter>
      </ModalWrapper>
    </Modal>
  );
};

export default DeletePlanModal;
